import { useMemo } from 'react';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import type { DevDocsBylineConnection } from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';

import { useIncomingLinksData } from '../../store/incomingLinksBridge.ts';
import {
	usePageAgentViewers,
	type UsePageAgentViewersResult,
} from '../../hooks/usePageAgentViewers.ts';
import {
	usePageAgentContributors,
	type UsePageAgentContributorsResult,
} from '../../hooks/usePageAgentContributors.ts';
import { usePagePeople } from '../../hooks/usePagePeople.ts';
import type { UsePagePeopleResult } from '../../hooks/usePagePeople.ts';
import { useRelatedContentData } from '../../store/relatedContentBridge.ts';
import { useDevDocsConnection } from '../../store/devDocsConnectionBridge.ts';
import { useOutgoingLinks } from '../../store/outgoingLinksBridge.ts';
import type { BylineContentType } from '../../utils/contentAri.ts';
import type { EnhancedSmartLink } from '../../utils/smartLinkTypes.ts';

export type SyncedFromData = {
	connection: DevDocsBylineConnection | null;
};

export type AppearsInData = {
	links: EnhancedSmartLink[];
	/** The rows of `links` read from the Teamwork Graph, without the Jira remote-link fallback. */
	twgLinks: EnhancedSmartLink[];
	loading: boolean;
	loadingMore: boolean;
	hasNextPage: boolean;
	endCursor: string | null;
	loadMore: () => void;
	error?: ApolloErrorInterop | undefined;
};

export type LinksToData = {
	links: EnhancedSmartLink[];
};

export type RelatedContentData = {
	/** False when the related-content experiment is off, and the section is absent. */
	enabled: boolean;
	links: EnhancedSmartLink[];
	loading?: boolean;
	error?: ApolloErrorInterop | undefined;
};

export type ConnectionSectionsData = {
	syncedFrom: SyncedFromData;
	appearsIn: AppearsInData;
	linksTo: LinksToData;
	relatedContent: RelatedContentData;
	/**
	 * The people the graph connects to this page. Unlike the slices above these come from AGG
	 * Graph Store rather than the SmartCard client, so they carry their own loading and error.
	 */
	people: UsePagePeopleResult;
	agentViewers: UsePageAgentViewersResult;
	agentContributors: UsePageAgentContributorsResult;
};

/**
 * Reads the byline's independent connection streams without fetching links again.
 * People, agent contributors and viewers are fetched by the panel; the modal shares this data object.
 */
export const useConnectionSectionsData = (
	contentId?: string,
	contentType?: BylineContentType,
): ConnectionSectionsData => {
	const { devDocsConnection } = useDevDocsConnection(contentId);
	const { incomingLinks: incoming } = useIncomingLinksData(contentId);
	const {
		links: incomingLinks,
		twgLinks,
		loading,
		loadingMore,
		hasNextPage,
		endCursor,
		loadMore,
		error: incomingError,
	} = incoming;
	const { outgoingLinks } = useOutgoingLinks(contentId);
	const { relatedContent } = useRelatedContentData(contentId);
	const {
		enabled: relatedContentEnabled,
		links: relatedContentLinks,
		loading: relatedContentLoading,
		error: relatedContentError,
	} = relatedContent;
	const agentContributors = usePageAgentContributors(contentId);
	const agentViewers = usePageAgentViewers(contentId ?? '');
	const people = usePagePeople(contentId, {
		contentType,
		agents: agentViewers.loading || agentViewers.error ? undefined : agentViewers.agents,
		agentContributors:
			agentContributors.availability === 'available' ? agentContributors.agents : undefined,
	});

	return useMemo(
		() => ({
			syncedFrom: { connection: devDocsConnection },
			appearsIn: {
				links: incomingLinks,
				twgLinks,
				loading,
				loadingMore,
				hasNextPage,
				endCursor,
				loadMore,
				error: incomingError,
			},
			linksTo: { links: outgoingLinks },
			relatedContent: {
				enabled: relatedContentEnabled,
				links: relatedContentLinks,
				loading: relatedContentLoading,
				error: relatedContentError,
			},
			people,
			agentViewers,
			agentContributors,
		}),
		[
			devDocsConnection,
			incomingLinks,
			twgLinks,
			loading,
			loadingMore,
			hasNextPage,
			endCursor,
			loadMore,
			incomingError,
			outgoingLinks,
			relatedContentEnabled,
			relatedContentLinks,
			relatedContentLoading,
			relatedContentError,
			people,
			agentViewers,
			agentContributors,
		],
	);
};
